<script lang="ts">
	import { browser } from "$app/env";
	import { invalidateAll } from "$app/navigation";
	import { navigating, page } from "$app/state";
	import { setContext } from "svelte";
	import { MediaQuery } from "svelte/reactivity";
	import { SegmentedControl, type Segment } from "@ishavskraft/shared";
	import { syncSessionValues } from "#lib/sessionValues.js";
	import { selectAreas } from "#lib/utils/areaUtils.js";
	import Breadcrumb from "#lib/Breadcrumb.svelte";
	import MetaData from "#lib/MetaData.svelte";
	import theTime from "@ishavskraft/the-time";
	import AreaControls from "../AreaControls.svelte";
	import HoursChart from "./HoursChart.svelte";
	import HoursTable from "./HoursTable.svelte";
	import PriceMinMax from "./PriceMinMax.svelte";
	import InputCheckbox from "#lib/InputCheckbox.svelte";
	import Info from "@ishavskraft/shared/components/icons/Info.svelte";
	import { getTitle, getDescription, getBreadcrumbs, getDayLabel, getAreaIntro } from "./_meta";
	import type { LayoutData } from "./$types";
	import type { DatasetSchema } from "#lib/types/metaData.js";
	import { seoToMetaData } from "#lib/types/metaData.js";
	import { Button } from "@ishavskraft/shared";
	import { localeRound } from "#lib/utils/math.js";
	import { hourStartMs } from "#lib/utils/norwayHour.js";
	import lang from "#lib/langStore.js";
	import PageBuilder from "#lib/PageBuilder.svelte";
	import type { Omraade } from "#lib/types/omraade.js";
	import { PortableText } from "@portabletext/svelte";
	import { useQuery } from "@sanity/sveltekit";
	import { normaliseSpotprisenBundle } from "#lib/fetch/spotprisenContent.js";
	import { selectSpotprisenDoc } from "../selectSpotprisenDoc";
	import type { AreaCode } from "#lib/types/spotprisenContent.js";

	type SegmentValue = "i-gar" | "i-dag" | "i-morgen";

	let { data, children }: { data: LayoutData; children?: import("svelte").Snippet } = $props();

	// Shared PageBuilder blocks read "page-lang" via getContext.
	setContext("page-lang", () => $lang ?? "nb");

	// Sanity Presentation live updates.
	// In published mode `data.sanity` is undefined → the store
	// is a no-op and we render the SSR'd `data.sanityBlocks` / `data.sanitySeo`.
	const q = useQuery(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		data.sanity?.query as any,
		data.sanity?.params,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		{ initial: data.sanity?.queryInitial as any }
	);
	const liveResolved = $derived.by(() => {
		if (!data.sanity?.query || !$q?.data) return null;
		const bundle = normaliseSpotprisenBundle($q.data, $lang ?? "nb");
		return selectSpotprisenDoc(bundle, (data.area?.value ?? null) as AreaCode | null);
	});
	const sanityBlocks = $derived(liveResolved?.blocks ?? data.sanityBlocks);
	const sanitySeo = $derived(liveResolved?.seo ?? data.sanitySeo);
	const sanityAreaInfo = $derived(liveResolved?.areaInfo ?? data.sanityAreaInfo);

	// URL has no area → implicit default (selectAreas[0]); URL stays
	// clean (no redirect).
	const currentArea = $derived(data.area ?? selectAreas[0]);
	const areaSuffix = $derived(data.area ? `/${data.area.seoSlug}` : "");
	const viewPrefix = $derived(data.view ? `/${data.view}` : "");
	const isTabell = $derived(data.presentation === "tabell");
	const tabellSuffix = $derived(isTabell ? "/tabell" : "");
	// In-flight intent: flips the toggle visual the instant the user clicks,
	// before the server load resolves. Falls back to the loaded state.
	const isTabellVisual = $derived(
		navigating.to ? navigating.to.url.pathname.endsWith("/tabell") : isTabell
	);

	// Detected-area URL: preserve view + presentation, swap in the new
	// area's slug. Passed through <AreaControls> to <DetectAreaButton>.
	const detectAreaHref = (area: Omraade) =>
		`/spotprisen${viewPrefix}/${area.seoSlug}${tabellSuffix}`;

	let segments = $derived<Segment<SegmentValue>[]>([
		{
			value: "i-gar",
			name: "I går",
			href: `/spotprisen/i-gar${areaSuffix}${tabellSuffix}`,
		},

		{
			value: "i-dag",
			name: "I dag",
			href: `/spotprisen${areaSuffix}${tabellSuffix}`,
		},

		{
			value: "i-morgen",
			name: "I morgen",
			href: `/spotprisen/i-morgen${areaSuffix}${tabellSuffix}`,
		},
	]);

	// Two stable destinations — each button knows where it goes, regardless
	// of which view is currently active. Graf is the canonical (no extra
	// segment); Tabell appends `/tabell` to the path.
	const grafHref = $derived(`/spotprisen${viewPrefix}${areaSuffix}`);
	const tabellHref = $derived(`/spotprisen${viewPrefix}${areaSuffix}/tabell`);

	// Derive from URL so SSR renders the right lead without waiting for
	// the SegmentedControl's $effect to sync on hydration.
	const activeSegment = $derived<SegmentValue>(
		segments.find((s) => s.href.replace(/\/$/, "") === page.url.pathname.replace(/\/$/, ""))
			?.value ?? "i-dag"
	);

	const verbByView: Record<SegmentValue, string> = {
		"i-gar": "var",
		"i-dag": "er",
		"i-morgen": "blir",
	};
	const avgOf = (priser: { pris: number }[]) =>
		priser.reduce((sum, p) => sum + p.pris, 0) / priser.length;

	// Lead sentence: trend vs previous day (i-dag vs i-går, i-morgen vs
	// i-dag) when available, plain average otherwise. i-går has no
	// comparison — we don't fetch two-days-ago.
	const lead = $derived.by(() => {
		if (!data.day || data.day.priser.length === 0) {
			return data.view === "i-morgen"
				? "Spotprisen for i morgen publiseres rundt kl. 13."
				: "Spotpriser for valgt dag er ikke tilgjengelig.";
		}
		const avg = avgOf(data.day.priser);
		const verb = verbByView[activeSegment];
		const subject = `Spotprisen for ${currentArea.value} ${currentArea.label}`;
		const avgStr = localeRound(avg);

		const prev = data.previousDay;
		if (!prev || prev.priser.length === 0) {
			return `${subject} ${verb} ${getDayLabel(data.view)} ${avgStr} øre/kWh i gjennomsnitt.`;
		}

		const delta = avg - avgOf(prev.priser);
		const deltaRounded = Math.round(delta);
		const prevName = data.view === "i-morgen" ? "i dag" : "i går";
		const trend =
			deltaRounded === 0
				? `${subject} ${verb} omtrent uendret fra ${prevName}`
				: `${subject} ${verb} i snitt ${Math.abs(deltaRounded)} øre ${delta > 0 ? "høyere" : "lavere"} enn ${prevName}`;
		return `${trend}; gjennomsnittet er ${avgStr} øre/kWh.`;
	});

	// Live "now" — ticks each minute (paused while the tab is hidden) so
	// the chart's current-hour marker tracks the clock without a refresh,
	// and we can `invalidateAll()` on hour rollover to pick up tomorrow's
	// prices once they publish (~13:00) and the new day's window after
	// midnight. Rollover goes on the hour's start instant — the Norwegian
	// hour stays "02" across the fall-back shift.
	let now = $state(theTime());

	$effect(() => {
		if (!browser) return;
		let lastHourStart = hourStartMs(now.getTime());
		// Resync + invalidate on rollover; a tab idle for hours can
		// otherwise sit on stale data.
		const sync = () => {
			now = theTime();
			const h = hourStartMs(now.getTime());
			if (h !== lastHourStart) {
				lastHourStart = h;
				void invalidateAll();
			}
		};
		const id = setInterval(() => {
			if (document.hidden) return;
			sync();
		}, 60_000);
		const onVis = () => {
			if (document.visibilityState !== "visible") return;
			sync();
		};
		document.addEventListener("visibilitychange", onVis);
		return () => {
			clearInterval(id);
			document.removeEventListener("visibilitychange", onVis);
		};
	});

	const highlightHourStart = $derived(data.view ? null : hourStartMs(now.getTime()));

	let showStromstotte = $state(false);

	const landscape = new MediaQuery("(min-width: 600px)");

	// Persist current path so /spotprisen/historikk's "Tilbake" button
	// can land back on the exact view/area/presentation. Not written
	// from /historikk itself (separate page component).
	$effect(() => {
		if (!browser) return;
		try {
			sessionStorage.setItem("spotprisen:lastPath", page.url.pathname);
		} catch {
			// Private-mode iOS / Safari can throw on sessionStorage access.
		}
	});

	// Persist URL-explicit area; shared key with stromavtale + legacy timespris.
	$effect(() => {
		if (!browser || !data.area) return;
		syncSessionValues({ area: data.area.value });
	});

	// CMS overrides when an editor has filled the seo field on the
	// resolved spotprisen_page doc (per-area, fallback chain handled in
	// selectSpotprisenDoc). URL-derived `_meta.ts` is the fallback.
	const title = $derived(sanitySeo?.title ?? getTitle(data.view, currentArea, isTabell));
	const description = $derived(
		sanitySeo?.description ?? getDescription(data.view, currentArea, isTabell)
	);
	const breadcrumbs = $derived(getBreadcrumbs(data.view, data.area));
	// Tabell variants serve the same data — canonical to the chart URL
	// so Google doesn't split signals between the two presentations.
	const canonical = $derived(
		isTabell ? `https://www.ishavskraft.no/spotprisen${viewPrefix}${areaSuffix}` : undefined
	);
	// Dataset JSON-LD for Google Dataset Search rich-result eligibility.
	const dataset = $derived.by<DatasetSchema | null>(() => {
		if (!data.day || data.day.priser.length === 0) return null;
		return {
			name: `Spotpris ${currentArea.value} (${currentArea.label}) ${getDayLabel(data.view)}`,
			description: `Time-for-time spotpris for ${currentArea.label} ${getDayLabel(data.view)}, fra Nord Pool. Inkluderer mva${currentArea.value === "NO4" ? " — NO4 (Nord-Norge) er fritatt mva" : ""}.`,
			spatialCoverage: currentArea.label,
			temporalCoverage: data.day.dato,
			variableMeasured: [{ name: "Spotpris", unitText: "øre/kWh" }],
		};
	});
	// H1 subheading. Area always present (resolved from URL or
	// `selectAreas[0]`) so the heading reflects what's on screen.
	const subheading = $derived(
		(data.view ? `${getDayLabel(data.view)} for ` : "for ") +
			`${currentArea.value} ${currentArea.label}`
	);
</script>

<MetaData
	data={{ title, description, canonical, ...seoToMetaData(sanitySeo) }}
	{breadcrumbs}
	{dataset}
	jsonLd
/>

<div
	class="relative after:content-[''] after:z--1 after:absolute after:inset-0 after:bg-glod-20 pb-8"
>
	<Breadcrumb {breadcrumbs} />

	<!-- Today: "Dagens strømpriser" (high-volume exact-match query slot).
	     Other days: bare "Strømpriser" with the day phrase in <small>,
	     so the main line doesn't flicker on day-toggle. -->
	<h1 class="mx-edge-x my-0">
		{#if data.view}
			Strømpriser
		{:else}
			<strong class="text-lysning">Dagens</strong> strømpriser
		{/if}
		<small class="block text-body font-body mt-1">{subheading}</small>
	</h1>

	<p class="mx-edge-x mt-2 text-sm tablet:text-base">{lead}</p>

	<div
		class="mt--5 px-edge-x pt-4 pb-10 contain-content relative after:z--1 after:content-[''] after:absolute after:bg-white after:rounded-[1rem] after:drop-shadow-md after:inset-[var(--s-4)_calc(var(--space-edge-x)-min(var(--space-edge-x)/1.5,var(--s-6)))_var(--s-8)] leading-tight"
	>
		<div class="inner-grid">
			<div class="controls grid gap-3 mt-2 tablet:mt-4">
				<SegmentedControl
					label="Velg dag"
					{segments}
					currentPath={page.url.pathname}
					class="my-0"
					keepScroll
				/>

				<AreaControls {currentArea} view={data.view} detectHref={detectAreaHref} />

				<div class="flex flex-wrap justify-between gap-2">
					<label class="flex gap-1 cursor-pointer button button-link min-w-0 p-0 min-h-[48px]">
						<InputCheckbox name="show-stromstotte" value="1" bind:state={showStromstotte} />
						<span class="text-sm">Vis m/strømstøtte</span>
					</label>
					<div class="flex gap-[.25ch]">
						<p class="m-0 sr-only">Vis som</p>
						<ul class="list-none p-0 m-0 flex">
							<li class={[]}>
								<Button
									href={grafHref}
									noScroll
									replaceState
									class={["min-w-0 text-sm min-h-[48px] p-0 items-start"]}
									aria-current={!isTabellVisual ? "page" : undefined}
									textStart
									link
								>
									<span
										class={[
											isTabellVisual && [
												"relative contain-content pr-[2.2em]",
												"after:content-[''] after:inline-block after:h-[1.25em] after:w-[1.75em] after:bg-primary-strong after:rounded-full",
												"after:absolute after:top-0 after:right-[.2em] after:z-1",
												"before:content-[''] before:inline-block before:h-[1em] before:w-[1em] before:bg-white before:rounded-full",
												"before:absolute before:top-[.125em] before:z-2",
												"before:right-[.325em]",
											],
										]}
									>
										Graf
									</span>
								</Button>
							</li>
							<li>
								<Button
									href={tabellHref}
									noScroll
									replaceState
									class={["min-w-0 text-sm min-h-[48px] p-0 items-start"]}
									aria-current={isTabellVisual ? "page" : undefined}
									textStart
									link
								>
									<span
										class={[
											!isTabellVisual && [
												"relative contain-content pl-[2.2em]",
												"after:content-[''] after:inline-block after:h-[1.25em] after:w-[1.75em] after:bg-primary-strong after:rounded-full",
												"after:absolute after:top-0 after:left-[.2em] after:z-1",
												"before:content-[''] before:inline-block before:h-[1em] before:w-[1em] before:bg-white before:rounded-full",
												"before:absolute before:top-[.125em] before:z-2",
												"before:left-[.325em]",
											],
										]}
									>
										Tabell
									</span>
								</Button>
							</li>
						</ul>
					</div>
				</div>
			</div>

			<div class="presentation mb-4 tablet:mt-4">
				{#if data.error}
					<p>Kunne ikke laste spotpriser. ({data.error})</p>
				{:else if !data.day}
					<p>
						Ingen priser for valgt dag enda{data.view === "i-morgen"
							? " (publiseres etter kl. 13)"
							: ""}.
					</p>
				{:else if isTabell}
					<div
						class="shadow-[inset_0_0_0_1px_hsl(from_currentcolor_h_s_l/.2)] rounded-[1rem] py-1px [&>*]:w-full tablet:[&>*]:w-auto w-fit"
					>
						<HoursTable day={data.day} />
					</div>
				{:else}
					<HoursChart
						day={data.day}
						{highlightHourStart}
						{showStromstotte}
						showYAxis={landscape.current}
					/>
				{/if}
			</div>

			<!-- This should be in grid below .controls on bigger screens -->
			<div>
				{#if data.day}
					<div
						class="info-table rounded-[1rem] shadow-[inset_0_0_0_1px_hsl(from_currentcolor_h_s_l/.2)] py-4 mt-2"
					>
						<PriceMinMax day={data.day} view={data.view} area={currentArea} />
					</div>
				{/if}

				{#if sanityAreaInfo}
					<div class="text-xs italic mt-4 mb-6">
						<PortableText value={sanityAreaInfo} />
					</div>
				{:else}
					<p class="text-xs italic mt-4 mb-6">{getAreaIntro(currentArea)}</p>
				{/if}
			</div>
		</div>

		<p class="grid grid-cols-[1.5rem_1fr] gap-1 mt-2">
			<Info classes="size-[1em] flex-none relative top-[.2em]" />
			<small>Prisene er oppgitt inkl. mva, bortsett fra NO4 (Nord-Norge) som er fritatt mva.</small>
		</p>
	</div>

	<section
		class="px-edge-x pt-4 pb-10 contain-content relative after:z--1 after:content-[''] after:absolute after:bg-white after:rounded-[1rem] after:drop-shadow-md after:inset-[var(--s-4)_calc(var(--space-edge-x)-min(var(--space-edge-x)/1.5,var(--s-6)))_var(--s-8)] [&>:first-child]:pt-3 [&>:last-child]:py-1"
	>
		<h2>Vil du se <strong class="text-primary-strong">historiske</strong> strømpriser?</h2>
		<p>
			Vil du se strømsprisen tilbake i tid, her kan du få en oversikt over strømprisen per år, og
			hva snittprisen var hver måned tilbake i tid.
		</p>
		<p>
			<Button primary href={`/spotprisen/historikk${areaSuffix}`} arrow>
				Gå til historiske priser
			</Button>
		</p>
	</section>
</div>

{@render children?.()}

<PageBuilder blocks={sanityBlocks} />

<style lang="postcss">
	@media (--tablet) {
		.inner-grid {
			grid-template-areas: "controls presentation" "info-table presentation";
			display: grid;
			/* Chart drives its own size at laptop+ (HoursChart pins a definite
			   height and lets `aspect-video` derive the width). Its column is
			   `auto`-sized to that derived width; the sidebar (controls +
			   info-table) takes whatever's left via `1fr`. `minmax(0, 1fr)`
			   so info-table can't push the sidebar past available space. */
			grid-template-columns: 20rem minmax(0, 1fr);
			grid-template-rows: auto 1fr;

			.controls {
				grid-area: controls;
			}
			.presentation {
				grid-area: presentation;
				margin-inline-start: var(--s-4);
				/* Chart fills the column via `w-full aspect-video` — column is
				   already `1fr`, no width reservation needed. Keep a min-height
				   for the empty / error / tabular states so the row doesn't
				   collapse before data arrives. */
				min-height: 360px;
			}
			.info-table {
				grid-area: info-table;
			}
		}
	}
</style>
